interface CellLike {
  hidden?: boolean;
  unit?: string | null;
  type?: string;
  colspan?: number;
  span?: number;
  full?: boolean;
}

type RowLike = { cols?: Record<string, CellLike> | CellLike[] };

const getColSpan = (cell: CellLike): number => {
  if (cell.colspan) return cell.colspan;
  if (cell.span) return 1 + cell.span;
  if (cell.full) return 999;
  return 1;
};

const getOrderedCells = (row: RowLike | undefined): CellLike[] => {
  if (!row?.cols) return [];
  return Array.isArray(row.cols) ? row.cols : Object.values(row.cols);
};

export const isBlankCell = (cell: CellLike): boolean =>
  cell.unit === "BLANK" || (cell.type === "col" && cell.hidden === true);

const isVisibleCell = (
  cell: CellLike,
  shouldHideElement: (cell: CellLike) => boolean,
  buttonOrientation?: "vertical" | "horizontal",
): boolean => {
  if (cell.hidden) return false;
  if (cell.unit === "space" || cell.type === "space") return false;
  if (buttonOrientation === "vertical" && isBlankCell(cell)) return false;
  if (shouldHideElement(cell)) return false;
  return true;
};

const rowHasVisibleCellAtColumn = (
  row: RowLike | undefined,
  columnIndex: number,
  shouldHideElement: (cell: CellLike) => boolean,
  buttonOrientation?: "vertical" | "horizontal",
): boolean => {
  if (!row?.cols || columnIndex < 0) return false;

  let col = 0;
  for (const cell of getOrderedCells(row)) {
    const span = getColSpan(cell);
    if (columnIndex >= col && columnIndex < col + span) {
      return isVisibleCell(cell, shouldHideElement, buttonOrientation);
    }
    col += span;
  }

  return false;
};

const rowHasHiddenCellAtColumn = (
  row: RowLike | undefined,
  columnIndex: number,
): boolean => {
  if (!row?.cols || columnIndex < 0) return false;

  let col = 0;
  for (const cell of getOrderedCells(row)) {
    const span = getColSpan(cell);
    if (columnIndex >= col && columnIndex < col + span) {
      return Boolean(cell.hidden);
    }
    col += span;
  }

  return false;
};

const spanHasVisibleCellAtRow = (
  rows: RowLike[],
  rowIndex: number,
  startCol: number,
  colspan: number,
  shouldHideElement: (cell: CellLike) => boolean,
  buttonOrientation?: "vertical" | "horizontal",
): boolean => {
  const row = rows[rowIndex];
  if (!row) return false;

  for (let col = startCol; col < startCol + colspan; col++) {
    if (
      rowHasVisibleCellAtColumn(row, col, shouldHideElement, buttonOrientation)
    ) {
      return true;
    }
  }

  return false;
};

export const getHiddenCellBorderFlags = (
  rows: RowLike[],
  rowIndex: number,
  startCol: number,
  colspan: number,
  shouldHideElement: (cell: CellLike) => boolean,
  buttonOrientation?: "vertical" | "horizontal",
) => {
  const row = rows[rowIndex];

  const hasBelow =
    rowIndex < rows.length - 1 &&
    spanHasVisibleCellAtRow(
      rows,
      rowIndex + 1,
      startCol,
      colspan,
      shouldHideElement,
      buttonOrientation,
    );

  const hasVisibleLeftOnSameRow =
    startCol > 0 &&
    rowHasVisibleCellAtColumn(
      row,
      startCol - 1,
      shouldHideElement,
      buttonOrientation,
    );

  const hasVisibleRightOnSameRow = rowHasVisibleCellAtColumn(
    row,
    startCol + colspan,
    shouldHideElement,
    buttonOrientation,
  );

  return {
    showTopBorder: false,
    showBottomBorder: hasBelow,
    showLeftBorder: hasVisibleLeftOnSameRow,
    showRightBorder: hasVisibleRightOnSameRow,
  };
};

export const getVisibleCellBorderFlags = (
  row: RowLike,
  columnOffset: number,
) => ({
  showLeftBorder:
    columnOffset > 0 && rowHasHiddenCellAtColumn(row, columnOffset - 1),
});

export { getColSpan };
